import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { ErrorState, Loading } from '@/components/ui/States'
import { jobFileUrl } from '@/features/documents/queries'
import { formatAgo, formatDateShort } from '@/lib/dates'
import { JOB_KIND_LABEL } from '@/lib/jobs'
import { QUESTIONNAIRE_STATUS_LABEL, questionnaireStatus } from '@/lib/questionnaire'
import { describeError, supabase } from '@/lib/supabase'
import { QuestionnaireForm } from './QuestionnaireForm'
import { STATUS_TONE, openQuestionnairePdf } from './QuestionnairesPage'
import { SendQuestionnaire } from './SendQuestionnaire'
import { clientName, questionnaireLink, useDeleteQuestionnaire, useSentQuestionnaire, useUpdateQuestionnaire } from './queries'
import { Field, Select } from '@/components/ui/Field'
import { useContacts } from '@/features/contacts/queries'

/** Opens a photo the couple uploaded with their answers (by its stored path, or, for older answers, its project file). */
async function openFile(fileId: string, path: string | undefined) {
  const tab = window.open('', '_blank')
  try {
    let filePath = path
    if (!filePath) {
      const { data, error } = await supabase.from('job_files').select('file_path').eq('id', fileId).single()
      if (error) throw error
      filePath = data.file_path
    }
    const url = await jobFileUrl(filePath)
    if (tab) tab.location.href = url
  } catch {
    tab?.close()
    toast.error('That file could not be opened.')
  }
}

/**
 * One questionnaire sent to a couple (29 Oct 2026): where it is, the buttons
 * to send it, the PDF, and their answers as they come in.
 */
export function QuestionnaireDetailPage() {
  const { id = '' } = useParams()
  const query = useSentQuestionnaire(id)
  const update = useUpdateQuestionnaire()
  const remove = useDeleteQuestionnaire()
  const navigate = useNavigate()

  if (query.isPending) return <Loading label="Opening the questionnaire" />
  if (query.isError)
    return (
      <Page>
        <ErrorState title="The questionnaire could not open" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )

  const row = query.data
  const status = questionnaireStatus(row)
  const client = clientName(row)
  const paths = new Map(
    Object.values(row.answers)
      .flatMap((value) => (Array.isArray(value) ? value : []))
      .map((file) => [file.id, file.path] as const),
  )

  return (
    <Page className="max-w-[60rem]">
      <PageHeader
        eyebrow="Questionnaire"
        title={client}
        meta={
          <>
            <Link to="/questionnaires" className="underline underline-offset-4">
              All questionnaires
            </Link>
            {row.job ? (
              <Link to={`/weddings/${row.job.id}`} className="underline underline-offset-4">
                The project, {formatDateShort(row.job.event_date)}
              </Link>
            ) : (
              <span>No project</span>
            )}
            <Badge tone={STATUS_TONE[status]}>{QUESTIONNAIRE_STATUS_LABEL[status]}</Badge>
          </>
        }
        actions={
          <>
            <Button onClick={() => void openQuestionnairePdf(row.id)}>Download PDF</Button>
            <a
              href={`${questionnaireLink(row.token)}?preview=1`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex h-[var(--size-control)] items-center rounded-md px-3.5 text-[length:var(--text-sm)] font-medium hover:bg-[var(--color-wash-strong)]"
            >
              See it as they do
            </a>
          </>
        }
      />

      <Section>
        <div className="flex flex-col gap-3 text-[length:var(--text-sm)]">
          <p className="text-taupe">
            {[
              row.sent_at ? `Sent ${formatAgo(row.sent_at)}` : 'Not sent yet',
              row.opened_at ? `opened ${formatAgo(row.opened_at)}` : null,
              row.completed_at
                ? `completed ${formatAgo(row.completed_at)}`
                : row.answered_at
                  ? `last answered ${formatAgo(row.answered_at)}, not submitted yet`
                  : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            .
          </p>
          {!row.completed_at ? <SendQuestionnaire questionnaire={row} /> : null}
          <ProjectPicker questionnaireId={row.id} contactId={row.contact?.id ?? null} jobId={row.job_id} />
          <div className="flex flex-wrap gap-2">
            {row.completed_at ? (
              <Button
                size="sm"
                variant="ghost"
                loading={update.isPending}
                onClick={() => {
                  if (window.confirm('Reopen it so they can change their answers? Their answers stay; they press Submit again when done.'))
                    update.mutate({ id: row.id, completed_at: null })
                }}
              >
                Reopen for changes
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              loading={remove.isPending}
              onClick={() => {
                if (!window.confirm(`Delete this questionnaire${row.answered_at ? ' and their answers' : ''}? Their link stops working.`)) return
                remove.mutate(row.id, { onSuccess: () => navigate('/questionnaires') })
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <QuestionnaireForm name={row.name} intro={null} questions={row.questions} answers={row.answers} onOpenFile={(fileId) => void openFile(fileId, paths.get(fileId))} />
      </Section>
    </Page>
  )
}

/** Adds the questionnaire to one of the client's projects, or takes it off. */
function ProjectPicker({ questionnaireId, contactId, jobId }: { questionnaireId: string; contactId: string | null; jobId: string | null }) {
  const contacts = useContacts(false)
  const update = useUpdateQuestionnaire()
  const projects = ((contacts.data ?? []).find((contact) => contact.id === contactId)?.jobs ?? []).filter(
    (job) => !job.archived_at || job.id === jobId,
  )
  if (!contactId) return null
  return (
    <Field label="Project" className="max-w-[22rem]" {...(projects.length === 0 ? { hint: 'This client has no projects yet.' } : {})}>
      <Select
        value={jobId ?? ''}
        disabled={update.isPending || projects.length === 0}
        onChange={(e) => update.mutate({ id: questionnaireId, job_id: e.target.value || null })}
      >
        <option value="">No project</option>
        {projects.map((job) => (
          <option key={job.id} value={job.id}>
            {(job.title?.trim() || JOB_KIND_LABEL[job.kind]) + ` · ${formatDateShort(job.event_date)}`}
          </option>
        ))}
      </Select>
    </Field>
  )
}
